import { useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import Delete from '@mui/icons-material/Delete';
import { IconLibrary } from '@product-framework/index';
import FileIcon from '@product-framework/components/FormFields/NotesAttachment/FileIcon';
import {
  getFileExtension,
  getFormattedFileSize,
  useDialog,
} from '@ui-framework/index';
import { getIssueAttachmentAccept, ISSUE_ATTACHMENT_TYPES } from './api';

function QueuedAttachmentList({
  files,
  onAddFiles,
  onRemoveFile,
  onChangeType,
  formatMessage,
  fileExtensions,
  uploading = false,
  onUpload,
}) {
  const inputRef = useRef();
  const [open, setOpen] = useState(false);
  const { confirmationRef } = useDialog();

  const confirmRemoveFile = index => {
    confirmationRef.current.init({
      title: formatMessage({
        id: 'DELETE_ATTACHMENT',
        defaultMessage: 'Delete Attachment',
      }),
      message: formatMessage({
        id: 'DO_YOU_WANT_TO_DELETE_THIS_ATTACHMENT',
        defaultMessage: 'Do you want to delete this attachment?',
      }),
      positiveButtonText: formatMessage({ id: 'YES', defaultMessage: 'Yes' }),
      negativeButtonText: formatMessage({
        id: 'CANCEL',
        defaultMessage: 'Cancel',
      }),
      positiveButtonIcon: <IconLibrary.Delete />,
      negativeButtonIcon: <IconLibrary.Cancel />,
      onChange: value => {
        if (value) {
          onRemoveFile(index);
        }
        confirmationRef.current.setOpen(false);
      },
    });
    confirmationRef.current.setOpen(true);
  };

  const uploadAndClose = async () => {
    if (!onUpload) {
      setOpen(false);
      return;
    }

    const uploaded = await onUpload();
    if (uploaded) {
      setOpen(false);
    }
  };

  const renderQueuedFile = (item, index) => (
    <Card
      key={`${item.file.name}-${item.file.size}-${item.file.lastModified}`}
      variant="outlined"
      sx={{ borderColor: 'primary.main', p: 1.25 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <FileIcon color="primary" ext={getFileExtension(item.file.name)} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" noWrap>
            {item.file.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {item.file.size == null
              ? '-'
              : getFormattedFileSize(item.file.size)}{' '}
            ·{' '}
            {formatMessage({
              id: 'PENDING_UPLOAD',
              defaultMessage: 'Pending upload',
            })}
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <Select
            value={item.attachmentType || 'OTHER'}
            onChange={event => onChangeType(index, event.target.value)}>
            {ISSUE_ATTACHMENT_TYPES.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {formatMessage({
                  id: `ISSUES_ATTACHMENT_TYPE_${option.value}`,
                  defaultMessage: option.label,
                })}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <IconButton size="small" onClick={() => confirmRemoveFile(index)}>
          <Delete color="error" fontSize="small" />
        </IconButton>
      </Stack>
    </Card>
  );

  return (
    <Box sx={{ mt: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle2" color="text.secondary">
          {formatMessage({ id: 'ATTACHMENTS', defaultMessage: 'Attachments' })}
        </Typography>
        <Button size="small" variant="outlined" onClick={() => setOpen(true)}>
          {formatMessage({
            id: 'ADD_ATTACHMENTS',
            defaultMessage: 'Add Attachments',
          })}
        </Button>
      </Stack>
      {files.length > 0 && (
        <Stack spacing={1} sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {formatMessage({
              id: 'SELECTED_ATTACHMENTS',
              defaultMessage: 'Selected attachments',
            })}
          </Typography>
          {files.map(renderQueuedFile)}
        </Stack>
      )}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm">
        <DialogTitle>
          {formatMessage({
            id: 'ADD_ATTACHMENTS',
            defaultMessage: 'Add Attachments',
          })}
        </DialogTitle>
        <DialogContent dividers>
          <Button
            size="small"
            variant="contained"
            onClick={() => inputRef.current?.click()}>
            {formatMessage({
              id: 'SELECT_FILES',
              defaultMessage: 'Select Files',
            })}
          </Button>
          <input
            ref={inputRef}
            hidden
            multiple
            type="file"
            accept={getIssueAttachmentAccept(fileExtensions) || undefined}
            onChange={event => {
              const input = event.target;
              onAddFiles(Array.from(input.files || []));
              input.value = '';
            }}
          />
          <Stack spacing={1} sx={{ mt: 2 }}>
            {files.map(renderQueuedFile)}
          </Stack>
        </DialogContent>
        <DialogActions display="flex" sx={{ justifyContent: 'center', my: 2 }}>
          <Button
            size="small"
            variant="contained"
            disabled={uploading || files.length === 0}
            startIcon={onUpload ? <IconLibrary.Upload /> : <IconLibrary.Add />}
            onClick={uploadAndClose}>
            {uploading
              ? formatMessage({
                  id: 'UPLOADING',
                  defaultMessage: 'Uploading...',
                })
              : formatMessage({
                  id: onUpload ? 'UPLOAD_ATTACHMENTS' : 'ADD_TO_REQUEST',
                  defaultMessage: onUpload
                    ? 'Upload Attachments'
                    : 'Add to Request',
                })}
          </Button>
          <Button
            size="small"
            variant="contained"
            color="secondary"
            disabled={uploading}
            startIcon={<IconLibrary.Cancel />}
            onClick={() => setOpen(false)}>
            {formatMessage({ id: 'CANCEL', defaultMessage: 'Cancel' })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default QueuedAttachmentList;
